/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { linkRoutes } from '../../config/routes/links';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin: 20px 0;
    text-align: justify;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  `${strapiAssetBaseUrl}/1_e7ad7d6adb.jpg`,
  `${strapiAssetBaseUrl}/2_cc7c5844aa.jpg`,
  `${strapiAssetBaseUrl}/3_facadbb199.jpg`,
  `${strapiAssetBaseUrl}/4_07b359fe60.jpg`,
  `${strapiAssetBaseUrl}/5_c2fed6c869.jpg`,
  `${strapiAssetBaseUrl}/6_49a86128e9.jpg`
];

const carouselImages2 = [
  {
    src: `${strapiAssetBaseUrl}/10_e52bf6c128.jpg`,
    caption:
      '[L-R] Happy Ongpauco-Tiu, Tim Yap, Kelly Misa, Focus Global Executive VP Loli Sy, Vanna Garcia, Vania Romoff, Patti Grandidge Herrera'
  },
  {
    src: `${strapiAssetBaseUrl}/11_f207f6430b.jpg`,
    caption: 'Jin Perez-Go'
  },
  {
    src: `${strapiAssetBaseUrl}/12_03c5d6300d.jpg`,
    caption:
      '[L-R] Focus Global President Stephen Sy, Wildflour CEO Ana Lorenzana de Ocampo'
  },
  {
    src: `${strapiAssetBaseUrl}/13_c17e80ddef.jpg`,
    caption: '[L-R] Kelly Misa, Vanna Garcia'
  },
  {
    src: `${strapiAssetBaseUrl}/14_f31a47ee10.jpg`,
    caption: 'Pinky Tobiano'
  },
  {
    src: `${strapiAssetBaseUrl}/15_f283115dbe.jpg`,
    caption: 'Angelo Comsti'
  }
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-5">
      <div>
        <h1>AN EVENING OF CULINARY PRECISION</h1>
        <p>Inside the Sub-Zero & Wolf x Antonio’s Pop-Up Experience.</p>
        <p className="p-2">
          In a fusion of innovation and artistry, Sub-Zero and Wolf officially
          launched The Table, their first-ever pop-up dining experience in
          collaboration with{' '}
          <Link href="https://subzero-wolf.com.ph/friends-of-the-kitchen/chef-tony-boy-escalante">
            Chef Tony Boy Escalante
          </Link>{' '}
          of Antonio’s. Held from May 26 to June 10, 2025 inside the{' '}
          <Link href="https://my.matterport.com/show/?m=Boe2AFEuzJe">
            Sub-Zero and Wolf Flagship Showroom
          </Link>{' '}
          in Makati, the space was transformed into an intimate restaurant,
          offering guests a rare taste of what culinary excellence looks like
          when driven by state-of-the-art kitchen equipment.
        </p>
        <Carousel
          imageURLs={carouselImages}
          aspectRatio="[3/2]"
          name="Exclusive Showroom"
        />
        <p className="pt-4">
          Beyond being just a regular dinner event, The Table was a full-sensory
          experience. From the warm ambient lighting and refined styling to the
          perfectly paced service, the space radiated quiet luxury. Guests were
          welcomed with the scent of expertly prepared ingredients, the clink of
          wine glasses, and the unmistakable energy of a chef-led dining room in
          motion.
        </p>
      </div>

      <div>
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/7_8b19a3f3b1.jpg`}
            width={500}
          />
          <Image
            alt="Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/8_c8736ea5a8.jpg`}
            width={500}
          />
          <Image
            alt="Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/9_3ac79df603.jpg`}
            width={500}
          />
        </div>
        <p className="pt-4">
          The intimate gathering brought together tastemakers from across the
          culinary and luxury lifestyle space for an evening that celebrated not
          just flavor, but also the quiet craft that begins in the kitchen.
          Among the guests were respected food and lifestyle editors,
          aspirational lifestyle influencers, and longstanding brand advocates
          who champion the same values of care, craft, and connection.
        </p>
      </div>

      <div>
        <Carousel
          imageURLs={carouselImages2}
          name="Exclusive Showroom"
          aspectRatio="[2/3]"
          swipper="size-full md:size-1/2"
        />
        <p className="pt-4">
          Notable attendees included celebrated chef and restaurateur{' '}
          <Link href="https://generations.subzero-wolf.com.ph/dining">
            Happy Ongpauco-Tiu
          </Link>{' '}
          of the Fresh Food for Generations campaign, clean eating advocate{' '}
          <Link href="https://fresh.subzero-wolf.com.ph/pillar/wellness/">
            Nikki Bharwani
          </Link>{' '}
          from the Fresh Food Matters campaign, and chef and operations lead{' '}
          <Link href="https://www.seedtoplate.subzero-wolf.com.ph/partner/good-food-community">
            Joyce Santos of Good Food Community
          </Link>
          . Their presence underscored the shared vision of nourishing not just
          the palate, but also the communities and philosophies behind every
          plate.
        </p>
      </div>

      <div className="flex flex-col">
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/16_5cbd45cb0c.jpg`}
            width={500}
          />

          <Image
            alt="Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/17_5ee4d7cb6c.jpeg`}
            width={500}
          />
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/18_9efdd45098.jpg`}
            width={500}
          />
        </div>
        <p className="mt-[0.9375rem] pt-2">
          “When I first began cooking, I quickly realized that the most
          memorable meals are not just about technique or ingredients—they’re
          about sharing. And it all begins in the kitchen: a space where care,
          craft, and connection quietly come together to create something
          meaningful,” shared Chef Tony Boy. This spirit of connection echoed
          throughout the night. Guests enjoyed a seven-course tasting menu
          meticulously designed to celebrate the synergy between culinary
          creativity and technical precision.
        </p>
      </div>

      <div className="flex flex-col -mt-10">
        <p className="pb-3">
          Each dish told a story about the ingredients, the farmers, and the
          possibilities that emerge when ingredients are honored from its very
          origin. Some of the seasonal produce was sourced from Good Food
          Community, a local social enterprise that partners with smallholder
          farmers to bring organically grown vegetables from farm to table.
          Sub-Zero and Wolf partnered in 2023 with Good Food Community to
          protect the harvests of their farmers through crop rain shelters.
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/19_ffff4b084d.jpg`}
            width={500}
          />

          <Image
            alt="Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/20_8e35ef2101.jpg`}
            width={500}
          />
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={700}
            src={`${strapiAssetBaseUrl}/21_200feeea88.jpg`}
            width={500}
          />
        </div>

        <p className="mt-[0.9375rem]">
          The tasting menu included:
          <ul className="mt-[0.8375rem] space-y-2">
            <li>
              <strong>Shrimp Cracker with Aioli</strong>: A delicate
              amuse-bouche that sets the tone for a meal built on layers of
              flavor.
            </li>
            <li>
              <strong>
                Smoked Herring with Ikura and Foie Gras Cornet with Raspberry
                Pearls
              </strong>
              : Bold yet balanced openers, paired with{' '}
              <span className="italic">
                Champagne Marie de Moy and Hibiki Master’s Select
              </span>
              , underscoring Chef Tony Boy’s deft hand with both land and sea.
            </li>
            <li>
              <strong>Emulsified Oysters with Seaweed and Nomad Caviar</strong>:
              An ultra-silky, oceanic course served chilled to perfection,
              thanks to Sub-Zero’s refrigeration keeping each element pristine.
            </li>
            <li>
              <strong>
                Tartar of Maya-Maya with Passionfruit-Tamarillo Veil, Coconut,
                and Coriander Oil
              </strong>
              : A tribute to local produce from Good Food Community, lifted by
              the crisp notes of{' '}
              <span className="italic">
                Jurtschitsch Grüner Veltliner 2023 and Lark Chinotto Citrus Cask
              </span>
              whisky.
            </li>
            <li>
              <strong>Flame-Torched Toro</strong>: An elegant dish served with
              Château de Chamirey Mercurey En Pierrelet 2022 and Highland Park
              12 Years, tuna belly balanced by savory-sweet Mediterranean
              flavors and a touch of umami-rich parmesan foam.
            </li>
            <li>
              <strong>
                Taogtog Berry Granita with Compressed Cucumber, Basil Oil, and
                Honey Tuile
              </strong>
              : A refreshing palate cleanser from Bauko, Mt. Province, a nod to
              Sub-Zero and Wolf’s enduring commitment to freshness.
            </li>
            <li>
              <strong>
                Tajima Striploin with Pepper Sauce, Pumpkin & Potato Gratin
              </strong>
              : A showstopping main course made possible by the accuracy and
              power of Wolf cooking appliances, paired with{' '}
              <span className="italic">
                Château La Nerthe Châteauneuf-du-Pape 2020 and Michter’s Bourbon
                10 Years
              </span>
              .
            </li>
            <li>
              <strong>Antonio’s Housemade Ice Cream Sundae Tray</strong>: A
              nostalgic dessert moment that brought the evening full circle.
              Generous scoops of housemade ice cream balanced out with
              french-pressed coffee from Mt. Canabawan, Negros and fresh
              tarragon tea, offering comfort, warmth, and a sweet sense of
              closure.
            </li>
          </ul>
        </p>
      </div>

      <div className="flex flex-col -mt-10">
        <p className="pb-3">
          Behind the menu’s brilliance was the presence of Sub-Zero and Wolf’s
          cutting-edge appliances. The Sub-Zero Classic Refrigerator and Freezer
          ensured that every ingredient remained in peak condition until the
          moment of preparation. Meanwhile, Wolf’s Induction Cooktop, M Series
          Oven, and Convection Steam Oven provided Chef Tony Boy and his team
          with the kind of consistent heat and precise temperature control
          needed to execute complex techniques with ease.
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Chef Demonstrating Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={300}
            src={`${strapiAssetBaseUrl}/22_576efef7b4.jpg`}
            width={500}
          />

          <Image
            alt="Spiced Bulkan Banana Cake with Kalingag Cashew Mousse"
            className="object-cover"
            height={300}
            src={`${strapiAssetBaseUrl}/23_b9bb0f7ff9.jpeg`}
            width={500}
          />
        </div>
        <p className="mt-[0.9375rem] pt-3">
          “Sub-Zero and Wolf have long been brands I admire for their precision,
          integrity, and the way they elevate the kitchen into a place of true
          expression. To cook with their appliances is to work with tools that
          respect both the ingredient and the intention behind it,” Chef Tony
          Boy said. “May this meal bring you comfort, connection, and a few
          moments to enjoy.”
        </p>
      </div>

      <div>
        <Image
          alt="Fish en Papillote with Beet Carpaccio and Oyster Mushroom Coconut Adlai Risotto"
          height={600}
          src={`${strapiAssetBaseUrl}/24_149c06ea6f.jpg`}
          width={960}
        />
        <p>
          You may follow{' '}
          <Link href="https://www.instagram.com/antoniostagaytay/">
            @antoniostagaytay
          </Link>{' '}
          on Instagram for more information on Antonio’s. To know more about
          Sub-Zero and Wolf products, you may check their Instagram page,{' '}
          <Link href="https://www.instagram.com/subzerowolfph/">
            @subzerowolfph
          </Link>
          . Focus Global, Inc. is the exclusive distributor of Sub-Zero and Wolf
          here in the Philippines.
        </p>
      </div>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '052625',
      news_title: 'An Evening of Culinary Precision'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
